/**
 * Browser presentation policy — the single place that answers two questions the
 * browser subsystem previously answered in five different files.
 *
 * 1. **Which backend runs the browser process?** A function of where the app
 *    under test lives, never of which client the human happens to be using. A
 *    desktop-local Chromium cannot reach a cloud runtime's loopback port, and a
 *    runtime Chromium cannot reach a laptop's dev server, so workload location
 *    decides and nothing else gets a vote.
 *
 * 2. **Which surface shows it to the human?** A function of what the client can
 *    host. Only a native host (the Electron renderer) can have an OS-level view
 *    positioned over a DOM slot; a web client has no such option and must be
 *    sent pixels.
 *
 * Those two questions are independent, which is why they are two functions.
 * Crossing them by hand at each call site is what produced the drift this
 * module exists to end: `nativeEmbeddedDisplayActive`, `remoteWebRtcDisplayActive`
 * and `interactiveMode` were derived separately in `BrowserPreviewPanel.logic.ts`,
 * `BrowserPreviewPanel.viewModel.ts` and `BrowserPreviewPanel.remoteWebRtcDisplay.ts`,
 * so no single test could state what the user was supposed to be looking at.
 *
 * `resolveBrowserPresentation` deliberately reproduces the mount behaviour that
 * shipped before this module existed, including the case where more than one
 * surface is mounted at once (see `primary`). Collapsing that overlap is a
 * later, separately reviewable change — this module only makes it visible.
 *
 * @module browserPresentation
 */
import type { BrowserDisplaySession, BrowserRenderMode } from "@cmmd-forge/contracts";

/** Where the app under test runs. Chooses the backend, by itself. */
export type BrowserWorkloadLocation = "local-project" | "cloud-runtime";

/**
 * What the viewing client can host. `native-host` is the Electron renderer,
 * which can park an OS-level view over a DOM slot; `web` is a plain browser
 * tab, which cannot and therefore can only be sent pixels.
 */
export type BrowserClientCapability = "native-host" | "web";

/** Which process actually drives Chromium. */
export type BrowserBackendKind = "embedded-native" | "runtime-playwright";

/**
 * A way of showing the browser to the human.
 *
 * Each lane has exactly **one** way to deliver live pixels, which is the whole
 * point of this union being short. A cloud runtime streams them; a desktop-local
 * runtime parks a native view. There is deliberately no third, lower-fidelity
 * live option to fall back to: the CDP JPEG screencast used to sit here as
 * `encoded-frames`, and because it painted something that looked exactly like a
 * working browser, a session whose real transport had died was indistinguishable
 * from one that was fine. Removing it does not make anything less able to work —
 * it makes a broken lane legible instead of plausible.
 *
 * - `native-overlay` — a real Chromium view positioned over a transparent DOM
 *   slot. Highest fidelity, no encoder in the path, native input. Desktop only.
 * - `remote-pixel` — an encoded video stream of a remote display (WebRTC). The
 *   only option that works for any client against a cloud runtime.
 * - `capture-still` — the last screenshot, no live input. What annotation mode
 *   and capture-only sessions show. Never stands in for a live surface.
 * - `unavailable` — nothing to show; the caller renders a status surface with
 *   `unavailableReason` explaining why.
 */
export type BrowserSurfaceKind =
  | "native-overlay"
  | "remote-pixel"
  | "capture-still"
  | "unavailable";

/** Why no live surface is available, for honest UI copy rather than a blank pane. */
export type BrowserPresentationUnavailableReason =
  | "no_display_session"
  | "display_failed"
  | "display_unsupported"
  | "network_unreachable"
  | "unsupported_host"
  | "client_cannot_host_native"
  /**
   * A native overlay is mounted but temporarily withdrawn because DOM is
   * layered above the panel. Nothing is wrong with the session; the surface is
   * simply not on screen while a palette or modal is open.
   */
  | "obscured_by_host_overlay";

/**
 * Where human pointer and keyboard input is routed.
 *
 * Note what is absent: `remote-pixel`. The WebRTC surface is output-only — it
 * accepts no pointer or keyboard props at all — so a streamed session takes its
 * input through a `command` layer mounted beneath the video, which translates a
 * click into a command against the runtime. Video out and input in are two
 * surfaces by design, and treating the pixel surface as an input target disables
 * input for every streamed session.
 *
 * This layer was previously called `encoded-frames`, because the same component
 * both painted JPEG screencast frames and captured input. Those are two jobs,
 * and only the painting half was the legacy route: deleting it while keeping
 * this one is why removing `encoded-frames` as a *surface* does not remove input
 * from a single streamed sprite session.
 */
export type BrowserInputTarget = "native" | "command" | "none";

export interface BrowserPresentationDecision {
  /**
   * The surface the human is actually looking at, resolved by fidelity order:
   * native-overlay > remote-pixel > capture-still > unavailable.
   *
   * More than one of the `*Active` flags below can be true at once, because that
   * is what the pre-existing panel did — it mounted the JPEG surface and the
   * WebRTC surface together and relied on stacking order to decide what was
   * visible. `primary` names the winner of that stack explicitly so callers can
   * mount one surface instead of inheriting an implicit z-order contract.
   */
  readonly primary: BrowserSurfaceKind;
  readonly nativeOverlayActive: boolean;
  readonly remotePixelActive: boolean;
  /** The input-capture layer beneath a stream. Not a picture; see `BrowserInputTarget`. */
  readonly commandInputActive: boolean;
  readonly inputTarget: BrowserInputTarget;
  /** True when the session is in a live, input-accepting mode (not capture, not annotating). */
  readonly interactive: boolean;
  /** Set only when `primary` is `unavailable`. */
  readonly unavailableReason?: BrowserPresentationUnavailableReason;
}

export interface BrowserBackendPolicyEntry {
  readonly workload: BrowserWorkloadLocation;
  readonly client: BrowserClientCapability;
  readonly backend: BrowserBackendKind;
  /** The surface this combination is expected to settle on once a display attaches. */
  readonly expectedSurface: BrowserSurfaceKind;
  /** Set when the combination cannot work at all. */
  readonly unsupportedReason?: BrowserPresentationUnavailableReason;
}

/**
 * The full workload × client matrix, exhaustive and asserted by test.
 *
 * The last row is the one that has to be stated rather than discovered: a web
 * client cannot reach a dev server running on someone's laptop, and it cannot
 * host a native view either, so there is no combination of transports that
 * makes it work. It is unsupported by construction, and the UI says so.
 */
export const BROWSER_BACKEND_POLICY: readonly BrowserBackendPolicyEntry[] = [
  {
    workload: "local-project",
    client: "native-host",
    backend: "embedded-native",
    expectedSurface: "native-overlay",
  },
  {
    workload: "cloud-runtime",
    client: "native-host",
    backend: "runtime-playwright",
    expectedSurface: "remote-pixel",
  },
  {
    workload: "cloud-runtime",
    client: "web",
    backend: "runtime-playwright",
    expectedSurface: "remote-pixel",
  },
  {
    workload: "local-project",
    client: "web",
    backend: "embedded-native",
    expectedSurface: "unavailable",
    unsupportedReason: "client_cannot_host_native",
  },
];

/**
 * The backend is a function of workload location alone.
 *
 * Client capability is deliberately not a parameter: a desktop user opening a
 * cloud runtime gets the runtime's Chromium, not a local one, because only the
 * runtime's Chromium can reach the runtime's ports.
 */
export function resolveBrowserBackend(input: {
  readonly workload: BrowserWorkloadLocation;
}): BrowserBackendKind {
  return input.workload === "local-project" ? "embedded-native" : "runtime-playwright";
}

/** The policy row for a workload/client pair. Total over the matrix. */
export function browserBackendPolicyFor(input: {
  readonly workload: BrowserWorkloadLocation;
  readonly client: BrowserClientCapability;
}): BrowserBackendPolicyEntry {
  const entry = BROWSER_BACKEND_POLICY.find(
    (candidate) => candidate.workload === input.workload && candidate.client === input.client,
  );
  if (!entry) {
    throw new Error(
      `No browser backend policy for workload ${input.workload} on client ${input.client}.`,
    );
  }
  return entry;
}

/**
 * Whether a remote pixel (WebRTC) display should be attached.
 *
 * Moved verbatim from `BrowserPreviewPanel.remoteWebRtcDisplay.ts` so the
 * predicate lives with the decision it feeds. `reconnecting` on an unreachable
 * network stays attachable on purpose: the surface is what drives the
 * reconnect, so detaching it strands the session in the failed state.
 */
export function shouldAttachRemotePixelDisplay(
  display: BrowserDisplaySession | undefined,
): boolean {
  const isReconnectingNetworkFailure =
    display?.status === "reconnecting" && display.reason === "network_unreachable";
  return (
    display?.transport === "remote-webrtc" &&
    display.status !== "failed" &&
    display.status !== "unsupported" &&
    (display.reason !== "network_unreachable" || isReconnectingNetworkFailure) &&
    display.reason !== "unsupported_host"
  );
}

function unavailableReasonFor(
  display: BrowserDisplaySession | undefined,
  client: BrowserClientCapability,
): BrowserPresentationUnavailableReason {
  if (!display) {
    return "no_display_session";
  }
  // Ask the policy table why this pairing cannot work, rather than restating its
  // rule here. `BROWSER_BACKEND_POLICY` used to be consulted by nothing but its
  // own test, so the matrix and the resolver were two independent statements of
  // one policy and could drift apart silently; routing the reason through
  // `browserBackendPolicyFor` makes the table the single source and gives it a
  // production call site.
  //
  // The workload is read back off the transport because the server chose the
  // transport *from* the workload: a desktop-local runtime embeds a native view,
  // and anything else streams. That makes the transport a faithful witness of
  // where the app under test is running, which is the only thing the table needs.
  const workload: BrowserWorkloadLocation =
    display.transport === "native-embedded" ? "local-project" : "cloud-runtime";
  const unsupportedReason = browserBackendPolicyFor({ workload, client }).unsupportedReason;
  if (unsupportedReason) {
    return unsupportedReason;
  }
  if (display.reason === "network_unreachable") {
    return "network_unreachable";
  }
  if (display.reason === "unsupported_host") {
    return "unsupported_host";
  }
  if (display.status === "unsupported") {
    return "display_unsupported";
  }
  if (display.status === "failed") {
    return "display_failed";
  }
  return "no_display_session";
}

export interface BrowserPresentationInput {
  /** What the viewing client can host. */
  readonly client: BrowserClientCapability;
  /**
   * The display session as reported by the server, already run through
   * preview normalization. Undefined before any display has been negotiated.
   */
  readonly display: BrowserDisplaySession | undefined;
  readonly renderMode: BrowserRenderMode | null;
  /** True while the user is placing an annotation, which freezes the surface. */
  readonly annotating: boolean;
  /**
   * Whether the session has enough state to paint anything at all — a tab, a
   * frame or a screenshot. False means the caller shows a status surface.
   *
   * This is the old `previewVm.canRenderCanvas`. It gates the two surfaces that
   * paint into the panel's own box, but deliberately not the remote pixel
   * surface, which the previous panel mounted as a sibling of that check.
   */
  readonly canRenderSurface: boolean;
  /**
   * True when DOM UI is layered over the panel — a command palette, a modal.
   *
   * This suppresses the native overlay and nothing else. A native OS view is
   * not a DOM node, so it cannot be painted under DOM siblings: it would cover
   * whatever opened above it. The pixel surfaces are ordinary elements and
   * stack correctly, so they are unaffected.
   */
  readonly obscuredByHostOverlay: boolean;
}

/**
 * Resolve what to mount, what is primary, and where input goes.
 *
 * Fidelity order is `native-overlay > remote-pixel > capture-still >
 * unavailable`, and the list has no live fallback in it on purpose. A lane
 * either delivers its own pixels or it reports why it cannot.
 *
 * - `nativeOverlayActive` — interactive, paintable, transport is
 *   `native-embedded`, and the client can actually host an OS-level view.
 * - `remotePixelActive` — interactive and `shouldAttachRemotePixelDisplay`.
 *   Deliberately not gated on `canRenderSurface`: the stream paints itself and
 *   does not need the panel to have a frame or screenshot first.
 * - `commandInputActive` — the input-capture layer beneath whichever live
 *   surface is up. It is mounted for a stream (which has no input path of its
 *   own) and withheld from a native overlay (which handles its own input).
 *
 * `capture-still` is reachable only when the session is **not** interactive —
 * annotation mode, or a capture-only render mode. It is never used to stand in
 * for a live surface, because a still that looks live is the failure this
 * module was rewritten to remove.
 *
 * A `web` client never reports `nativeOverlayActive`, even if the server hands
 * it a `native-embedded` transport. The server is not supposed to negotiate a
 * native display for a client that cannot host one; enforcing it here means a
 * server-side regression surfaces as an honest `unavailable` rather than a
 * native surface mounted into a plain browser tab.
 */
export function resolveBrowserPresentation(
  input: BrowserPresentationInput,
): BrowserPresentationDecision {
  const interactive = input.renderMode === "interactive" && !input.annotating;
  const nativeDisplayAvailable =
    interactive &&
    input.canRenderSurface &&
    input.client === "native-host" &&
    input.display?.transport === "native-embedded";
  const nativeOverlayActive = nativeDisplayAvailable && !input.obscuredByHostOverlay;
  const remotePixelActive = interactive && shouldAttachRemotePixelDisplay(input.display);
  // A native overlay handles its own input; every other live surface takes it
  // through the command layer. Withheld when nothing live is up, so a status
  // surface cannot silently swallow clicks.
  const commandInputActive = interactive && !nativeOverlayActive && remotePixelActive;

  const inputTarget: BrowserInputTarget = nativeOverlayActive
    ? "native"
    : commandInputActive
      ? "command"
      : "none";

  const flags = {
    nativeOverlayActive,
    remotePixelActive,
    commandInputActive,
    inputTarget,
    interactive,
  } as const;

  if (nativeOverlayActive) {
    return { primary: "native-overlay", ...flags };
  }
  if (remotePixelActive) {
    return { primary: "remote-pixel", ...flags };
  }
  // A frozen still is for the modes that ask for one. It is never a stand-in for
  // a live surface, so an interactive session with no working transport falls
  // through to `unavailable` rather than showing the last screenshot as if it
  // were the page.
  if (!interactive && input.canRenderSurface) {
    return { primary: "capture-still", ...flags };
  }
  return {
    primary: "unavailable",
    ...flags,
    unavailableReason: nativeDisplayAvailable
      ? "obscured_by_host_overlay"
      : unavailableReasonFor(input.display, input.client),
  };
}
